import { Section, SectionHeader } from "@/components/ui/Section";
import { motion } from "framer-motion";
import { Compass, Layers3, Workflow, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const phases = [
  {
    icon: Compass,
    label: "Understand",
    title: "Business, execution, and AI leverage opportunities.",
    body: "We map the actual operating reality of the company: where decisions live, where work stalls, where AI and systems can compound.",
  },
  {
    icon: Layers3,
    label: "Architect",
    title: "Systems thinking, workflows, scalability, AI integration strategy.",
    body: "Product, platform, and AI architected as one operating system. Built for the next stage, not just the next sprint.",
  },
  {
    icon: Workflow,
    label: "Execute",
    title: "Lean senior-led implementation with governance.",
    body: "Small senior teams, clear cadence, real ownership. We ship with the rigor of a consulting practice, not a vendor queue.",
  },
  {
    icon: Activity,
    label: "Optimize",
    title: "Continuous iteration, visibility, and operational intelligence.",
    body: "Dashboards, decision systems, and AI loops that keep the business legible to the founder as it scales.",
  },
];

export function HowWeWorkPreviewSection() {
  return (
    <Section background="muted">
      <SectionHeader
        eyebrow="How we work"
        variant="dark"
        title="A consulting-grade operating model, applied to your company."
        subtitle="A four-phase rhythm that turns founder ambition into systems, execution, and operational leverage."
      />

      <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* connecting hairline on desktop */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute left-6 right-6 top-12 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
        />

        {phases.map((p, i) => (
          <motion.div
            key={p.label}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: i * 0.08, ease: [0.21, 0.47, 0.32, 0.98] }}
            viewport={{ once: true, margin: "-60px" }}
            className="relative group rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-7 hover:border-accent/35 hover:bg-white/[0.07] transition-all duration-500"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 rounded-xl bg-white/[0.06] border border-white/10 grid place-items-center group-hover:bg-accent/15 group-hover:border-accent/40 transition-colors">
                <p.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/45">
                0{i + 1}
              </span>
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent mb-2">
              {p.label}
            </p>
            <h3 className="text-[17px] font-display font-semibold text-white leading-snug mb-3">
              {p.title}
            </h3>
            <p className="text-sm text-white/60 leading-[1.7]">{p.body}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 flex justify-center">
        <Link
          to="/how-we-work"
          className="group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.04] backdrop-blur-md px-6 py-3 text-sm font-medium text-white hover:bg-white/[0.10] transition-all duration-300"
        >
          See the full operating model
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
        </Link>
      </div>
    </Section>
  );
}